/**
 * RequestQueueManager - Manages concurrent request limits
 *
 * This singleton class ensures that only a specified number of requests
 * can be active at any given time. Additional requests are queued and
 * processed when slots become available.
 */

// Fallback when no valid concurrency cap is supplied. A queue with 0 (or NaN)
// concurrency never drains — processQueue() always early-returns, so every enqueued
// request hangs forever. This happens when MANUFACTURING_COST_CALCULATOR_MAX_CONCURRENT
// is unset: the view renders `ENV[...].to_i` => 0 => maxConcurrent 0.
const DEFAULT_MAX_CONCURRENT = 3;

class RequestQueueManager {
  constructor(maxConcurrent = parseInt(window.RequestQueueManagerC)) {
    const parsedMaxConcurrent = Number(maxConcurrent);
    this.maxConcurrent = parsedMaxConcurrent > 0 ? parsedMaxConcurrent : DEFAULT_MAX_CONCURRENT;
    this.activeRequests = 0;
    this.queue = [];
    this.completedRequests = 0;
    this.totalRequests = 0;
    this.progressCallbacks = [];
    // Bumped by reset()/clear(). A request is tagged with the generation it was enqueued in;
    // a completion only counts toward progress if its generation still matches (see processQueue).
    this.generation = 0;
  }

  /**
   * Add a request to the queue
   * @param {Function} requestFn - Function that executes the request and returns a Promise
   * @returns {Promise} - Promise that resolves when the request completes
   */
  enqueue(requestFn) {
    this.totalRequests++;
    this.notifyProgress();

    return new Promise((resolve, reject) => {
      const queuedRequest = {
        requestFn,
        resolve,
        reject,
        generation: this.generation
      };

      this.queue.push(queuedRequest);
      this.processQueue();
    });
  }

  /**
   * Process the next request in the queue if capacity is available
   */
  processQueue() {
    // Check if we have capacity and pending requests
    if (this.activeRequests >= this.maxConcurrent || this.queue.length === 0) {
      return;
    }

    // Get the next request from the queue
    const queuedRequest = this.queue.shift();
    this.activeRequests++;

    // Only completions from the current generation count toward progress. reset()/clear() (run at
    // the start of each fetch) bump the generation, so a request still in flight from a previous
    // fetch does NOT increment the new batch's count — which used to push completedRequests past
    // totalRequests and leave the "Loading Layout Combinations" overlay stuck at 100%.
    const countsTowardProgress = () => queuedRequest.generation === this.generation;

    // Execute the request
    queuedRequest.requestFn()
      .then((result) => {
        this.activeRequests--;
        if (countsTowardProgress()) { this.completedRequests++; }
        this.notifyProgress();
        queuedRequest.resolve(result);
        this.processQueue(); // Process next request
      })
      .catch((error) => {
        this.activeRequests--;
        if (countsTowardProgress()) { this.completedRequests++; }
        this.notifyProgress();
        queuedRequest.reject(error);
        this.processQueue(); // Process next request even on error
      });
  }

  /**
   * Get current queue status (useful for debugging)
   */
  getStatus() {
    return {
      activeRequests: this.activeRequests,
      queuedRequests: this.queue.length,
      maxConcurrent: this.maxConcurrent,
      completedRequests: this.completedRequests,
      totalRequests: this.totalRequests
    };
  }

  /**
   * Register a callback to be notified of progress updates
   * @param {Function} callback - Function that receives progress info {completed, total, percentage}
   * @returns {Function} - Function to remove this callback
   */
  onProgress(callback) {
    this.progressCallbacks.push(callback);
    // Immediately notify with current progress
    this.notifyProgress();

    // Return unsubscribe function
    return () => this.removeProgressCallback(callback);
  }

  /**
   * Remove a progress callback
   * @param {Function} callback - The callback to remove
   */
  removeProgressCallback(callback) {
    this.progressCallbacks = this.progressCallbacks.filter(cb => cb !== callback);
  }

  /**
   * Notify all progress callbacks with current progress
   */
  notifyProgress() {
    // Defensive: never report completed above total. The overlay hides on `completed === total`,
    // so an overshoot (which the generation guard now prevents at the source) would otherwise leave
    // it stuck at 100%. Keeping the reported counts honest is the invariant the overlay relies on.
    const completed = Math.min(this.completedRequests, this.totalRequests);
    const rawPercentage = this.totalRequests > 0 ? Math.round((completed / this.totalRequests) * 100) : 0;
    const progressInfo = {
      completed,
      total: this.totalRequests,
      percentage: Math.min(100, rawPercentage)
    };

    this.progressCallbacks.forEach(callback => {
      try {
        callback(progressInfo);
      } catch (error) {
        console.error('Error in progress callback:', error);
      }
    });
  }

  /**
   * Reset progress tracking
   */
  reset() {
    this.completedRequests = 0;
    this.totalRequests = 0;
    // Invalidate any in-flight requests from the previous batch so their completion can't count
    // against the new batch (see processQueue's generation guard).
    this.generation++;
    this.notifyProgress();
  }

  /**
   * Clear all pending requests and reset progress
   */
  clear() {
    this.queue = [];
    this.reset();
  }
}

// Export the class as default
export default RequestQueueManager;

// Export a default singleton instance for general use
export const defaultRequestQueueManager = new RequestQueueManager(  parseInt(window.RequestQueueManagerC) );
